/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse, stringify } from 'qs';
import {
  Table,
  Button,
  Tooltip,
  Input,
  Popconfirm,
  Spin,
  message,
  Select,
  Tag,
} from 'antd';
import { PlusCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { captureAnalytics } from '../../../actions/preload';
import {
  MANUAL,
  categoryLabel,
  triggerCell,
} from '../../../utils/taskTemplateAutomation';

const { Search } = Input;

// What an Ordering Provider template does when the document's provider can't
// be matched in EMA, one sentence per configured fallback in the order the
// worker applies them. Empty when the template has none (the upload fails).
export const assignmentFallbackLines = (template = {}) => {
  const lines = [];
  if (template.fallback_to_user) {
    lines.push('If no staff group matches, assigns the provider\'s own EMA user.');
  }
  const fallbackNames = template.fallback_assign_to_as_array || [];
  if (fallbackNames.length > 0) {
    lines.push(`If no one matches, assigns ${fallbackNames.join('; ')}.`);
  }
  return lines;
};

const parseOptions = {
  ignoreQueryPrefix: true,
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

export class ManageTaskTemplates extends Component {
  // antd's Select calls onSearch once per keystroke, so tracking every call
  // would bill one analytics event per character. Only the empty-to-typed
  // transition is a distinct user action, so hold the last reported value here:
  // a continuous typing run reports once, and clearing reports once. Kept off
  // state because nothing renders from it.
  reportedTriggerFilterHasQuery = false;

  constructor(props) {
    super(props);
    this.state = {
      emaTaskTemplates: null,
      currentPage: 1,
      pageSize: 10,
      selectedTemplate: '',
      searchValue: '',
      // The search box writes searchValue on every keystroke but only submits
      // on Enter or when another filter changes, so the name filter the table
      // is actually showing lives here.
      submittedSearch: '',
      priority: null,
      searchedCategories: [],
      documentCategories: [],
      documentCategory: null,
      scopeType: null,
      scopeNames: [],
      scopeName: null,
      scopeOptionNames: {},
      userDepartments: [],
      scopeNameDisabled: true,
    };
  }

  componentDidMount = async (page = 1) => {
    const { getTaskTemplates, getUserInfo, getDocumentCategories } = this.props;
    await getDocumentCategories();
    await getUserInfo();
    const { departments, organizationDetails } = this.props;
    this.setState({
      departments,
      organizationDetails,
    });
    const userDepartments = [];
    departments.forEach((dept) => {
      if (!userDepartments.includes(dept.name)) {
        userDepartments.push(dept.name);
      }
    });
    userDepartments.sort();
    this.setState({
      userDepartments,
      scopeOptionNames: {
        User: [localStorage.getItem('m8_name')],
        Department: userDepartments,
        Organization: [organizationDetails.current_organization],
      },
    });

    const params = {};
    params.page = page;
    const query = stringify(params, stringifyOptions);
    await getTaskTemplates(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      documentCategories: this.props.allDocumentCategories.categories,
      searchedCategories: this.props.allDocumentCategories.categories,
    });
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      emaTaskTemplates: nextProps.allEmaTaskTemplates.ema_task_templates,
    });
  }

  getQueryData = async (page = 1) => {
    const { location, headers, getTaskTemplatesByQuery } = this.props;
    const totalPages = (headers ? parseInt(headers.total_count, 10) : 1);
    let params = {};
    if (location.search) {
      params = parse(location.search, parseOptions);
      // eslint-disable-next-line no-param-reassign
      page = parseInt((params.page <= totalPages ? params.page : 1), 10);
    }
    params.page = page;
    params.page_size = this.state.pageSize;
    const query = stringify(params, stringifyOptions);
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });
  }

  // The active filters as query params, derived from component state only.
  // paramTools.params is rebuilt by UserProfile from the URL on every render and
  // this page never writes filters to the URL, so it cannot be trusted to still
  // hold them by the time a later refresh runs.
  activeFilterParams = () => {
    const {
      departments,
      documentCategory,
      priority,
      scopeName,
      scopeType,
      submittedSearch,
    } = this.state;
    const params = {};
    if (submittedSearch && submittedSearch.trim()) {
      params.contains = submittedSearch;
    }
    if (priority) {
      params.priority = priority;
    }
    if (documentCategory) {
      params.document_category_id = documentCategory;
    }
    if (scopeName) {
      params.owner_type = scopeType;
      if (scopeType === 'Organization') {
        params.owner_id = localStorage.m8_org_id;
      } else if (scopeType === 'User') {
        params.user_owner = decodeURIComponent(localStorage.m8_uid);
      } else if (scopeType === 'Department') {
        const department = (departments || []).find(d => d.name === scopeName);
        if (department) params.owner_id = department.id;
      }
    }
    return params;
  };

  // Re-fetch the table exactly as the user left it (search, filters, page,
  // page size) after the add/edit dialog saves. An edit can move the only row
  // on the last page out of the filtered set, leaving the current page empty
  // while earlier pages still have matches, so fall back to the last valid page.
  refreshTemplates = async () => {
    const { getTaskTemplatesByQuery } = this.props;
    const { currentPage, pageSize } = this.state;
    const fetchPage = page => getTaskTemplatesByQuery(
      stringify({ ...this.activeFilterParams(), page, page_size: pageSize }, stringifyOptions),
    );
    await fetchPage(currentPage);
    const { allEmaTaskTemplates, headers } = this.props;
    const rows = allEmaTaskTemplates?.ema_task_templates || [];
    if (rows.length > 0 || currentPage <= 1) return;
    const totalCount = parseInt(headers?.total_count, 10) || 0;
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    const page = Math.min(currentPage - 1, lastPage);
    this.setState({ currentPage: page });
    await fetchPage(page);
  };

  openTemplateModal = (template, { isDuplicate = false } = {}) => {
    const { openModal, isAdmin } = this.props;
    const { userDepartments, organizationDetails } = this.state;

    const templateData = isDuplicate
      ? {
        ...template,
        id: undefined,
        task_name: `Duplicate of ${template.task_name}`,
        document_category_id: null,
        emr_category: null,
      }
      : template;

    return openModal({
      type: 'ADD_TASK_TEMPLATE',
      data: {
        template: templateData,
        userDepartments,
        userOrganization: organizationDetails.current_organization,
        isAdmin,
        isDuplicateMode: isDuplicate,
        duplicatedFromCategory: isDuplicate ? template.emr_category : null,
        onSaved: this.refreshTemplates,
      },
    });
  }

  handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    const { pageSize: previousPageSize } = this.state;
    const page = (pageSize !== previousPageSize) ? 1 : current;
    if (pageSize !== previousPageSize) {
      captureAnalytics('task_templates_page_size_changed', { page_size: pageSize });
    }
    this.setState({
      currentPage: page,
      pageSize,
    });
    const { paramTools, getTaskTemplatesByQuery: fetchTemplates } = this.props;
    const { params } = paramTools;
    params.page = page;
    params.page_size = pageSize;
    const query = `${stringify(params, stringifyOptions)}`;

    fetchTemplates(query);
  };

  searchTemplates = (value, paramType) => {
    const { paramTools, getTaskTemplatesByQuery } = this.props;
    const { params } = paramTools;
    const {
      departments,
      documentCategory,
      priority,
      scopeName,
      scopeType,
      searchValue,
    } = this.state;
    if (!searchValue) {
      delete params.contains;
    }
    if (!priority) {
      delete params.priority;
    }
    if (!documentCategory) {
      delete params.document_category_id;
    }
    if (!scopeName) {
      delete params.owner_type;
      delete params.owner_id;
    }
    if (scopeType !== 'User') {
      delete params.user_owner;
    }
    if (searchValue) {
      params.contains = searchValue;
    }
    if (priority) {
      params.priority = priority;
    }
    if (documentCategory) {
      params.document_category_id = documentCategory;
    }
    if (scopeName) {
      params.owner_type = scopeType;
      if (scopeType === 'Organization') {
        params.owner_id = localStorage.m8_org_id;
      } else if (scopeType === 'User') {
        delete params.owner_id;
        params.user_owner = decodeURIComponent(localStorage.m8_uid);
      } else if (scopeType === 'Department') {
        const depId = departments.find(d => d.name === scopeName).id;
        params.owner_id = depId;
      }
    }
    params.page = 1;
    params.page_size = this.state.pageSize;
    const query = `${stringify(params, stringifyOptions)}`;
    // Every path here sends contains=searchValue, so whatever is in the box is
    // submitted by a dropdown change too. Record it on every path.
    if (paramType === 'templateName') {
      this.setState({ searchValue: value, submittedSearch: value, currentPage: 1 });
    } else {
      this.setState({ submittedSearch: searchValue, currentPage: 1 });
    }
    const emptySearch = !searchValue.trim() && !priority && !documentCategory && !scopeName;
    if (emptySearch) {
      return getTaskTemplatesByQuery(stringify({ page: 1, page_size: this.state.pageSize }, stringifyOptions));
    }
    getTaskTemplatesByQuery(query);
  }

  deleteTaskTemplate = async (template) => {
    const { deleteTaskTemplate } = this.props;
    await deleteTaskTemplate(template.id);
    const { error, errorCode } = this.props;
    if (error && errorCode === 403) {
      message.error('Cannot delete Task Template. You need permission to perform this action');
    }

    this.getQueryData();
  }

  handleScopeChange = async (e) => {
    if (e === undefined) {
      await this.setState({
        scopeType: null,
        scopeNames: [],
        scopeName: null,
        scopeNameDisabled: true,
      });
      await this.searchTemplates('', 'scope');
    } else {
      const { scopeOptionNames } = this.state;
      await this.setState({ scopeType: e, scopeNames: scopeOptionNames[e] });
      if (e === 'User' || e === 'Organization') {
        const scopeName = this.state.scopeNames[0];
        await this.setState({ scopeName, scopeNameDisabled: true });
        await this.searchTemplates(scopeName, 'scope');
      }
      if (e === 'Department') {
        await this.setState({ scopeName: null, scopeNameDisabled: false });
      }
    }
  };

  handleScopeNameChange = async (e) => {
    if (e === undefined) {
      await this.setState({ scopeName: null });
      await this.searchTemplates('', 'scope');
    } else {
      await this.setState({ scopeName: e });
      await this.searchTemplates(e, 'scope');
    }
  }

  handlePriorityChange = async (value) => {
    if (value === undefined) {
      await this.setState({ priority: null });
      await this.searchTemplates('', 'priority');
    } else {
      await this.setState({ priority: value });
      await this.searchTemplates(value, 'priority');
    }
  }

  // Filters the category objects themselves. Matching on the EMA name alone and
  // rebuilding by name lookup would drop one half of a same-named pair and could
  // never match an AI category name.
  handleDocumentCategorySearch = async (e) => {
    const unfilteredObjects = this.state.documentCategories;
    const hasQuery = Boolean(e);
    if (hasQuery !== this.reportedTriggerFilterHasQuery) {
      this.reportedTriggerFilterHasQuery = hasQuery;
      // Only whether a query was typed, never the query itself — a filter box is
      // free text and must not carry anything identifying into PostHog.
      captureAnalytics('task_templates_trigger_filter_searched', { has_query: hasQuery });
    }
    if (e) {
      const query = e.toLowerCase();
      const filteredObject = unfilteredObjects.filter(category => (
        (category.emr_category || '').toLowerCase().includes(query)
        || (category.ai_category_name || '').toLowerCase().includes(query)
      ));
      await this.setState({ searchedCategories: filteredObject });
    } else {
      await this.setState({ searchedCategories: unfilteredObjects });
    }
  }

  handleDocumentCategoryChange = async (e) => {
    if (e === undefined) {
      await this.setState({ documentCategory: null });
      await this.searchTemplates('', 'documentCategory');
    } else {
      await this.setState({ documentCategory: e });
      await this.searchTemplates(e, 'documentCategory');
    }
  }

  render() {
    const isTebra = localStorage.getItem('m8_auto_upload_emr') === 'Kareo';
    const {
      emaTaskTemplates,
      currentPage,
      pageSize,
      selectedTemplate,
      searchValue,
      priority,
      documentCategory,
      searchedCategories,
      scopeType,
      scopeName,
      scopeNameDisabled,
    } = this.state;
    const isEditing = template => template.task_name === selectedTemplate;
    const {
      headers,
      isLoading,
      isAdmin,
      autoTaskRouting,
    } = this.props;
    const userName = localStorage.getItem('m8_name');
    const canSeeRoutingRulesTab = isAdmin && autoTaskRouting;
    const totalResults = headers ? headers.total_count : 1;
    let columns = [
      {
        title: (
          <Tooltip title="Custom name of your template that displays on Send to Chart and Create Task Modals.">
            {isTebra ? 'Note Template Name' : 'Task Template Name'}
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'task_name',
        dataIndex: 'task_name',
      },
      {
        title: 'Type',
        dataIndex: 'task_type',
        key: 'task_type',
      },
      {
        title: 'Details',
        dataIndex: 'task_details',
        key: 'task_details',
      },
      {
        title: 'Assign To',
        dataIndex: 'assign_to_as_string',
        key: ':assign_to_as_string',
        render: (assignTo, template) => {
          const fallbackLines = assignmentFallbackLines(template);
          if (fallbackLines.length === 0) return assignTo;

          return (
            <span>
              {assignTo}
              {' '}
              <Tooltip title={fallbackLines.map((line) => <div key={line}>{line}</div>)}>
                <Tag style={{ marginLeft: '4px' }}>Fallback</Tag>
              </Tooltip>
            </span>
          );
        },
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
      },
      {
        title: (
          <Tooltip title="Either visible for the User, Department, or Organization.">
            Scope
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        dataIndex: 'scope_name',
        key: 'scope_name',
      },
      {
        title: (
          <Tooltip title="The template runs automatically when task automation is enabled, the fax matches the trigger category, and its fax line belongs to this template's audience. Templates without a trigger can be selected manually or used by routing rules.">
            Trigger
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        dataIndex: 'emr_category',
        key: 'emr_category',
        render: (emrCategory, template) => {
          const cell = triggerCell(template, emrCategory);

          if (cell.kind === MANUAL) {
            return <span style={{ color: cell.color }}>{cell.text}</span>;
          }

          const tag = (
            <Tag color={cell.color}>
              {cell.text}
            </Tag>
          );

          return cell.tooltip
            ? <Tooltip title={cell.tooltip}>{tag}</Tooltip>
            : tag;
        },
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
      },
      {
        title: '',
        key: 'edit',
        align: 'left',
        render: (_, template) => {
          const editable = isEditing(template);
          return editable ? (
            <span>
              <Button
                // eslint-disable-next-line no-script-url
                href="javascript:;"
                onClick={() => this.save(template)}
                style={{
                  marginRight: 8,
                }}
              >
                Save
              </Button>
              <Popconfirm title="Cancel edit without saving?" onConfirm={() => this.setState({ selectedTemplate: '' })}>
                <a>Cancel</a>
              </Popconfirm>
            </span>
          ) : (
            <span>
              {
                ((isAdmin || (template.scope_name === userName)))
                && (
                  <>
                    <Button disabled={selectedTemplate !== ''} onClick={() => this.openTemplateModal(template, { isDuplicate: true })} style={{ marginRight: 10 }}>
                      Duplicate
                    </Button>
                    <Button disabled={selectedTemplate !== ''} onClick={() => this.openTemplateModal(template)} style={{ marginRight: 10 }}>
                      Edit
                    </Button>
                  </>
                )
              }
              {
              ((isAdmin || (template.scope_name === userName)))
                && (
                <Popconfirm title="Delete this Template? (This action is permanent and cannot be reversed. " onConfirm={() => this.deleteTaskTemplate(template)}>
                  <a>Delete</a>
                </Popconfirm>
                )
              }
            </span>
          );
        },
      },
    ];

    if (isLoading) return (<Spin size="large" />);
    columns = columns.filter(column => column.dataIndex !== 'department');
    if (isTebra) columns = columns.filter((column) => !['task_type', 'task_details', 'priority'].includes(column.dataIndex));

    return (
      // eslint-disable-next-line object-curly-newline
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '16px 40px 40px', textAlign: 'left' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, marginBottom: 16 }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: '#181818', margin: 0, textAlign: 'left' }}>
            {isTebra ? 'Manage Note Templates' : 'Manage Task Templates'}
          </h2>
          <p style={{ color: '#5c5c5c', fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'left' }}>
            {isTebra ? 'Upload incoming faxes and create assigned notes in Tebra, or reuse note templates manually.' : 'Create EMA tasks automatically when faxes arrive — or save task setups for manual reuse.'}
            <br />
            {`A template with a trigger fires when a received fax is categorized with that document category; templates without one appear in Send to Chart and Create ${isTebra ? 'Note' : 'Task'} for manual use.`}
            {' '}
            <Tooltip
              placement="bottom"
              title={(
                <div style={{ maxWidth: 340, lineHeight: 1.5 }}>
                  A template&apos;s audience controls who can pick it — only you, a department,
                  or the whole organization — and which fax lines can trigger it.
                  {canSeeRoutingRulesTab && (
                    <>
                      {' '}
                      {`For more advanced rules (label conditions, priorities), use the ${isTebra ? 'Note' : 'Task'} Routing tab.`}
                    </>
                  )}
                  {' '}
                  Note: automatic tasks only fire when task automation is enabled for
                  your organization, the AI classifies the fax into the trigger
                  category, and the fax arrives on a line owned by the template&apos;s
                  audience.
                </div>
              )}
            >
              <a style={{
                color: '#662c88',
                fontWeight: 500,
                cursor: 'help',
                textDecoration: 'none',
                borderBottom: '1px dotted currentColor',
              }}
              >
                How this works
              </a>
            </Tooltip>
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <Button
            icon={<PlusCircleOutlined />}
            type="primary"
            onClick={this.openTemplateModal}
          >
            {`Add ${isTebra ? 'Note' : 'Task'} Template`}
          </Button>
          <div>
            <Select
              allowClear
              showSearch
              placeholder="Scope"
              value={scopeType}
              style={{ width: 120, marginRight: 10 }}
              onChange={this.handleScopeChange}
            >
              <Select.Option value="User">User</Select.Option>
              <Select.Option value="Department">Department</Select.Option>
              <Select.Option value="Organization">Organization</Select.Option>
            </Select>
            <Select
              allowClear
              showSearch
              placeholder={scopeNameDisabled ? 'Scope Name' : 'Department'}
              value={scopeName}
              disabled={scopeNameDisabled}
              style={{ width: 150, marginRight: 10 }}
              onChange={this.handleScopeNameChange}
              options={this.state.scopeNames.map(name => ({ label: name, value: name }))}
            />
            <Select
              allowClear
              showSearch
              placeholder="Trigger category"
              value={documentCategory}
              onSearch={this.handleDocumentCategorySearch}
              onChange={this.handleDocumentCategoryChange}
              filterOption={false}
              style={{ width: 180, marginRight: 10 }}
            >
              {searchedCategories.map(e => (
                <Select.Option key={e.id} value={e.id}>
                  {categoryLabel(e.emr_category, e.ai_category_name)}
                </Select.Option>
              ))}
            </Select>
            {!isTebra && (
              <Select
                allowClear
                showSearch
                style={{ width: 120, marginRight: 10 }}
                placeholder="Priority"
                value={priority}
                onChange={this.handlePriorityChange}
              >
                <Select.Option value="Normal">Normal</Select.Option>
                <Select.Option value="High">High</Select.Option>
                <Select.Option value="STAT">STAT</Select.Option>
              </Select>
            )}

            <Search
              placeholder="Template name"
              value={searchValue}
              onChange={e => this.setState({ searchValue: e.target.value })}
              onSearch={() => this.searchTemplates(searchValue, 'templateName')}
              style={{ width: 200, height: '32px', marginRight: '10px' }}
            />
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <Table
            size="small"
            dataSource={emaTaskTemplates}
            columns={columns}
            rowKey={record => record.id}
            bordered
            onChange={this.handleTableChange}
            pagination={{
              position: ['bottomCenter'],
              total: totalResults,
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
          />
        </div>
      </div>
    );
  }
}

ManageTaskTemplates.defaultProps = {
  isLoading: false,
  data: [],
  allEmaTaskTemplates: {},
  departments: [],
  organizationDetails: {},
  headers: {
    page: 1,
    'per-page': '10',
    total_count: 1,
  },
  paramTools: {
    push: () => {},
    params: {},
    stringify: () => {},
    stringifyOptions: {},
  },
  errorCode: '',
  autoTaskRouting: false,
};

ManageTaskTemplates.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  headers: PropTypes.shape({
    page: PropTypes.number,
    'per-page': PropTypes.string,
    total_count: PropTypes.number,
  }),
  getTaskTemplates: PropTypes.func.isRequired,
  paramTools: PropTypes.shape({
    params: PropTypes.object,
    stringify: PropTypes.func,
    stringifyOptions: PropTypes.object,
    push: PropTypes.func,
  }),
  location: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  departments: PropTypes.array,
  organizationDetails: PropTypes.object,
  deleteTaskTemplate: PropTypes.func.isRequired,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  emaTaskTemplates: PropTypes.object.isRequired,
  allDocumentCategories: PropTypes.object.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  allEmaTaskTemplates: PropTypes.object,
  error: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  autoTaskRouting: PropTypes.bool,
};

export default connect(state => ({
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  emaTaskTemplates: state.auth.data,
  allDocumentCategories: state.userProfile.allDocumentCategories,
  isLoading: state.userProfile.isLoading,
  isAdmin: state.userProfile.isAdmin,
  headers: state.userProfile.headers,
  error: state.userProfile.error,
}))(ManageTaskTemplates);
